import { Component, OnInit, ChangeDetectionStrategy, signal, computed, inject, input, output } from "@angular/core";
import { AppConfigService } from "../../../../core-ui/services/app.config.service";
import { CompositionService } from "../../../../core-ui/services/composition.service";
import { MediaPlayerService } from "../../../../core-ui/services/media.player.service";
import { DeviceService } from "../../../../core-ui/services/device.service";
import {
  AgentParam,
  IComposition,
  IMediaPlayerPlayRequest,
} from "../../../../core-ui/models";
import { CompositionStateEnum } from "../../../../core-ui/core-ui.enums";
import { zoomInOut } from "../../../../core-ui/core-ui.animations";
import { NotificationService } from "../../../../core-ui/services/notification.service";
import { UserService } from "../../../../core-ui/services/user.service";
import { extractErrorMessage } from "../../../../core-ui/services/utils";

@Component({
  selector: "app-composition-row",
  templateUrl: "./composition-row.component.html",
  styleUrls: ["./composition-row.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [zoomInOut],
  standalone: false,
})
export class CompositionRowComponent implements OnInit {
  private compositionService = inject(CompositionService);
  private config = inject(AppConfigService);
  private notification = inject(NotificationService);
  private mediaPlayerService = inject(MediaPlayerService);

  readonly composition = input<IComposition>(undefined);

  readonly composeRequested = output<IComposition>();
  readonly cancelRequested = output<IComposition>();
  readonly navigate = output<string>();
  readonly removed = output<IComposition>();

  downloadUrl = signal<string>(undefined);
  canRequestDownload = computed(
    () =>
      !this.downloadUrl() &&
      (this.isSupervisor() ||
        this.isAdmin() ||
        (this.isAgent() &&
          this.config.agentParamEnabled(
            AgentParam.COMPOSITION_DOWNLOAD_ENABLED
          )))
  );
  playUrl: string;
  lang: string;
  loadingLink = signal(false);
  loadingRemove = signal(false);
  isConfirmOpen = signal(false);
  isSupervisor = signal(false);
  isAdmin = signal(false);
  isAgent = signal(false);
  canRemove = signal(false);

  isRemoveEnabled = computed(() => this.isSupervisor() && this.canRemove());

  stateCompleted = CompositionStateEnum.completed;

  constructor() {
    const config = this.config;
    const user = inject(UserService);

    this.lang = config.language;
    this.isSupervisor.set(user.isSupervisor);
    this.isAdmin.set(user.isAdmin);
    this.isAgent.set(user.isAgent);
    this.canRemove.set(
      config.agentParamEnabled(AgentParam.COMPOSITION_DELETE_ENABLED)
    );
  }

  ngOnInit() {}

  async play() {
    if (!this.playUrl) {
      const response = await this.compositionService.getUrl(
        this.composition(),
        "inline"
      );
      this.playUrl = response.url;
    }

    const request: IMediaPlayerPlayRequest = {
      sourceUrl: this.playUrl,
      sourceType: this.composition().type,
      sourceFileName: this.composition().name,
    };
    this.mediaPlayerService.play(request);
  }

  async download() {
    await this.getDownloadLink();
  }

  async getDownloadLink(): Promise<string> {
    if (!this.downloadUrl()) {
      try {
        this.loadingLink.set(true);
        const response = await this.compositionService.getUrl(
          this.composition(),
          "attachment"
        );
        this.downloadUrl.set(response.url);
      } catch (ex) {
        this.notification.success("Could not retrieve download link", {
          body: ex.message || ex,
        });
      } finally {
        this.loadingLink.set(false);
      }
    }
    return this.downloadUrl();
  }

  retry() {
    this.composeRequested.emit(this.composition());
  }

  cancel() {
    this.cancelRequested.emit(this.composition());
  }

  go() {
    this.navigate.emit(this.composition().conversationId);
  }

  async remove() {
    try {
      this.loadingRemove.set(true);
      await this.compositionService.deleteComposition(
        this.composition().conversationId,
        this.composition().id
      );
      this.removed.emit(this.composition());
    } catch (ex) {
      this.notification.error("Composition", { body: extractErrorMessage(ex) });
    } finally {
      this.loadingRemove.set(false);
      this.isConfirmOpen.set(false);
    }
  }

  get canDownload() {
    return true; /*!this.device.isIFrame;*/
  }

  get canPlay() {
    return !DeviceService.isMobile;
  }

  get isCancelVisible() {
    const composition = this.composition();
    return (
      composition.state === CompositionStateEnum.processing ||
      composition.state === CompositionStateEnum.submitted ||
      composition.state === CompositionStateEnum.queued ||
      composition.state === CompositionStateEnum.accepted ||
      composition.state === CompositionStateEnum.downloading ||
      composition.state === CompositionStateEnum.preprocessing ||
      composition.state === CompositionStateEnum.finalizing
    );
  }

  get icon() {
    switch (this.composition().type) {
      case "AUDIO":
        return "file-audio";
      case "VIDEO":
        return "file-video";
    }
  }

  get iconTooltip() {
    switch (this.composition().type) {
      case "AUDIO":
        return "Audio file";
      case "VIDEO":
        return "Video file";
    }
  }
}
